import React from "react";
import { useLocation, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { CreateRiskDialog } from "./Create";
import { HeaderMenu, Col, usePub } from "@neumetric/blueprint-lib/components";
import { NMModuleRespondersFlow } from "@neumetric/blueprint-lib/flows";
import { useNavigate, Outlet } from "react-router-dom";
import { ImportRisks } from "./Import";
import { UploadRiskPopup } from "./Upload";
import { ExportRiskDialog } from "./Export";
import { getDepartments, getActiveUsers, getCurrentUser } from "@neumetric/blueprint-lib/store";
import {
  getDepartmentResponders,
  riskActions,
  getDeptResponderByDept,
  getDecisionList,
  getSeverityList,
  getRiskStatusList,
  isResponderSetForAllDepts,
  getOrgRisksCount,
} from "../../store";

const getTabItems = (isArchiveTab) => {
  if (isArchiveTab) {
    return [
      {
        label: "Archive",
        id: "A",
      },
    ];
  }
  let tabs = [
    { label: "Register", id: "R" },
    { label: "Stats", id: "S" },
    {
      label: "List",
      id: "L",
      subMenu: [
        { label: "Threat", id: "T" },
        { label: "Vulnerability", id: "V" },
        { label: "Asset", id: "A" },
        { label: "Rating", id: "R" },
        { label: "Reference", id: "Ref" },
      ],
    },
  ];
  return tabs;
};

const MenuItems = [
  {
    label: "Import Risks (Blueprint)",
    id: "import-risk",
    dialogId: "import-risk",
    variant: "CONTRAST",
    icon: "ADD",
  },
  {
    label: "Upload Risks (CSV)",
    id: "upload-risk",
    dialogId: "upload-risk",
    variant: "CONTRAST",
    icon: "UPLOAD",
  },
  {
    label: "Export Risks (CSV)",
    id: "export-risk",
    dialogId: "export-risk",
    variant: "CONTRAST",
    icon: "UPLOAD",
  },
  {
    label: "Risk Responders",
    id: "risk-responder",
    dialogId: "risk-responder",
    icon: "ASSIGN",
    variant: "CONTRAST",
    shouldCloseOnClick: true,
  },
];

const ArchiveMenuItems = {
  label: "Archived Risks",
  id: "archived-risks",
  icon: "OPEN_FOLDER",
  variant: "CONTRAST",
  shouldCloseOnClick: true,
};

const getTab = (pathname) => {
  const paths = pathname.split("/");
  const tab = paths[2] || "R";
  return tab;
};

const getSubmenu = (pathname) => {
  const paths = pathname.split("/");
  const submenu = paths[3] || "T";
  return submenu;
};

export const RegisterHomeScreen = (props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const publish = usePub();
  const location = useLocation();
  const decisionList = useSelector(getDecisionList);
  const severityList = useSelector(getSeverityList);
  const statusList = useSelector(getRiskStatusList);
  const departments = useSelector(getDepartments);
  const deptResponders = useSelector(getDepartmentResponders);
  const orgRisksCount = useSelector(getOrgRisksCount);
  const deptRespondersSetForAllDepts = useSelector(isResponderSetForAllDepts);
  const user = useSelector(getCurrentUser);
  const users = useSelector(getActiveUsers);
  const _modRespondersRef = React.useRef(null);
  const isArchiveTab = location.pathname.endsWith("/M/A");
  const [state, setState] = React.useState({
    activeTab: getTab(location.pathname),
    showNewRisk: false,
    activeSubmenu: "T",
    menuTarget: null,
    selectedDialogId: null,
  });
  const showIndicator = state.activeTab !== "L";

  const getFilterActions = React.useCallback(() => {
    const filters = [
      { attribute: "decision", label: "Decision", options: decisionList },
      { attribute: "severity", label: "Severity", options: severityList },
      { attribute: "risk_status", label: "Status", options: statusList },
    ];
    return filters.map((filter) => ({
      label: filter.label,
      disabled: !deptRespondersSetForAllDepts,
      filters: filter.options.map((option) => ({
        text: option.label,
        value: option.id,
        dotIconName: option.dotIconName,
      })),
      typeRepresentation: "DOT",
      onConfirm: handleFilterConfirm.bind(null, filter.attribute),
    }));
  }, [deptRespondersSetForAllDepts]);

  const handleFilterConfirm = (attribute, selected) => {
    const data = { attribute, selected };
    publish("RISK_FILTER_CHANGED", data);
  };

  const getMenuItems = React.useCallback(() => {
    const items = MenuItems.map((item) => ({ ...item }));
    if (!isArchiveTab && orgRisksCount > 0) {
      items.push({ ...ArchiveMenuItems });
    }
    return items;
  }, [isArchiveTab, orgRisksCount]);

  React.useEffect(() => {
    dispatch(riskActions.fetchRiskDeptResponders());
  }, []);

  React.useEffect(() => {
    updateState({ activeTab: isArchiveTab ? "A" : getTab(location.pathname) });
  }, [isArchiveTab]);

  const handleTabChange = (id) => {
    updateState({ activeTab: id });
    navigate(`/M/${id}`);
  };

  const handleSubmenuClick = (id) => {
    // updateState({ activeTab: id });
    navigate(`/M/L/${id}`);
  };

  const updateState = (newState) => {
    setState({ ...state, ...newState });
  };

  const handleNewRiskClick = React.useCallback(() => {
    if (!deptRespondersSetForAllDepts) return;
    updateState({ showNewRisk: true });
  }, [deptRespondersSetForAllDepts]);

  const handleCloseNewRisk = (_, isCreated) => {
    if (isCreated) {
      publish("RISK_FILTER_CHANGED", {});
    }
    updateState({ showNewRisk: false });
  };

  const handleMenuItemClick = (e, menu) => {
    if (menu.id === "archived-risks") {
      dispatch(riskActions.clearRiskList());
      navigate("A");
      return;
    }
    setState((_) => ({
      ..._,
      menuTarget: null,
      selectedDialogId: menu ? menu.dialogId : null,
    }));
  };

  const getDeptResponders = () => {
    if (!deptResponders) return [];
    return departments.map((dept) => {
      const deptResp = getDeptResponderByDept(deptResponders, dept.id);
      return {
        ...dept,
        responder: deptResp && deptResp.responder,
      };
    });
  };

  const handleAddResponders = (departmentId, userId) => {
    const action = riskActions.updateRiskDeptResponder({
      departmentId,
      userId,
    });
    action.onComplete = () => {
      _modRespondersRef.current.updateState({ submitting: false, stage: null });
    };
    action.onFailure = () => {
      _modRespondersRef.current.updateState({ submitting: false, stage: null });
    };
    dispatch(action);
  };

  const handleRemoveResponders = (departmentId) => {
    const action = riskActions.removeRiskDeptResponder({
      departmentId,
    });
    action.onComplete = () => {
      _modRespondersRef.current.updateState({ submitting: false, stage: null });
    };
    action.onFailure = () => {
      _modRespondersRef.current.updateState({ submitting: false, stage: null });
    };
    dispatch(action);
  };

  return (
    <Col className="w-100 h-100 overflow-hide screen-pad bg-contrast" align="start">
      <Col className="w-100 mt-8" style={{ minHeight: "40px", paddingLeft: "0.8rem" }} align="start" justify="center">
        {isArchiveTab && (
          <Link to="R">
            <span className="ff-exo2 line-22 fs-14 fw-400 text-action">Register</span>
          </Link>
        )}
      </Col>
      <HeaderMenu
        initialTab={state.activeTab}
        initialSubmenu={getSubmenu(location.pathname)}
        showIndicator={showIndicator}
        tabItems={getTabItems(isArchiveTab)}
        onTabChange={handleTabChange}
        onSubmenuClick={handleSubmenuClick}
        classes={{ root: "mb-4" }}
        {...((state.activeTab === "R" || isArchiveTab) && {
          newObject: {
            label: "New Risk",
            onClick: handleNewRiskClick,
            disabled: !deptRespondersSetForAllDepts,
          },
          menu: {
            items: getMenuItems(),
            disabled: !deptRespondersSetForAllDepts,
            onMenuClick: (item) => {
              handleMenuItemClick(null, item);
            },
          },
          filter: {
            actions: getFilterActions(),
          },
        })}
      />
      <React.Fragment>
        <Outlet />
      </React.Fragment>
      {state.showNewRisk && <CreateRiskDialog onClose={handleCloseNewRisk} />}
      {/* IMPORT RISK POPUP */}

      <ImportRisks
        open={state.selectedDialogId === "import-risk"}
        onClose={() => setState((prevState) => ({ ...prevState, selectedDialogId: null }))}
      />

      <UploadRiskPopup
        open={state.selectedDialogId === "upload-risk"}
        onClose={() => setState((prevState) => ({ ...prevState, selectedDialogId: null }))}
      />
      <ExportRiskDialog
        open={state.selectedDialogId === "export-risk"}
        onClose={() => setState((prevState) => ({ ...prevState, selectedDialogId: null }))}
      />
      <NMModuleRespondersFlow
        moduleId="RISK_MODULE_RESPONDER_3_"
        ref={_modRespondersRef}
        visible={state.selectedDialogId === "risk-responder"}
        title="Risk"
        relatedObjectName="Risk"
        departments={getDeptResponders()}
        users={users || []}
        currentUser={user}
        allowRemove={false}
        onClose={() => setState((prevState) => ({ ...prevState, selectedDialogId: null }))}
        onResponderConfirm={handleAddResponders}
        onResponderRemove={handleRemoveResponders}
      />
    </Col>
  );
};
